import { useRef } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import * as Sentry from '@sentry/react';
import {
    CreateTeamParams,
    createCampaignTeam,
    fetchCampaignTeamById,
    updateCampaignTeam,
} from 'api/campaigns';
import { uploadFile } from 'api/media';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { FileRefMeta, UploadFile, UploadRef, UseFormUpload } from 'components/upload';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface TeamFormValues {
    name: string;
    goal: string;
    slug: string;
    logo: FileRefMeta[];
}

const CampaignTeamDrawerForm = () => {
    const toast = useToast();
    const { teamId: possibleTeamId } = useParams<{ teamId?: string }>();
    const teamId = possibleTeamId ? parseInt(possibleTeamId) : undefined;
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id!;

    const { data: team } = useSuspenseQuery({
        queryKey: campaignQueryKeys.teamById(campaignId, teamId!),
        queryFn: () =>
            conditionallyFetchOrNull(() => fetchCampaignTeamById(campaignId, teamId!), !!teamId),
    });

    const methods = Form.useForm<TeamFormValues>({
        defaultValues: {
            name: team?.name ?? '',
            goal: team?.goal ?? '',
            slug: team?.slug ?? '',
            logo: team?.logo ? [{ id: 'logo', url: team.logo }] : [],
        },
    });

    const curriedUpstreamRequest = (params: CreateTeamParams) =>
        teamId
            ? updateCampaignTeam(campaignId, teamId, params)
            : createCampaignTeam(campaignId, params);

    const { isPending: isLoading, mutate: submit } = useMutation({
        mutationFn: async (values: TeamFormValues) => {
            const goal = unmaskNumber(values.goal);

            // lazily evaluate the picker for consistent file blob refs
            const logo = methods.getValues('logo')[0] as UploadFile | undefined;
            let nextLogo: string | null = null;
            // use existing logo if remote urls are the same
            if (team?.logo && team.logo === logo?.getMetadata('url')) {
                nextLogo = team.logo;
            } else if (logo) {
                try {
                    const { data } = await uploadFile(logo.file);
                    nextLogo = data.url;
                } catch (e) {
                    Sentry.captureException(e);
                }
            }

            return curriedUpstreamRequest({
                name: values.name,
                goal: goal > 0 ? goal : null,
                logo: nextLogo,
                slug: values.slug.length > 0 ? values.slug : null,
            });
        },
        onSuccess: () => {
            const isEdit = !!teamId;
            toast.success(`${isEdit ? 'Updated' : 'Created'} team successfully!`);
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.teams(campaignId),
                type: !isEdit ? 'all' : undefined,
            });
            goBack();
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Unknown error occurred');
        },
    });

    const { formState, setValue, path, control, handleSubmit } = methods;
    const ref = useRef<UploadRef>(null);

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<TeamFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>{teamId ? 'Update' : 'Create new'} team</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body className={'overflow-x-hidden'}>
                    <div className={'flex flex-col gap-y-6'}>
                        <Form.Container>
                            <Form.Label name={path('name')}>
                                Team name
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={'name'}
                                rules={{ required: 'Please enter a team name' }}
                            >
                                <Form.TextInputForm
                                    placeholder={'The Red Team'}
                                    onChange={e => {
                                        if (!teamId && !formState.dirtyFields.slug) {
                                            setValue('slug', e.target.value);
                                        }
                                    }}
                                />
                            </Form.TextInputRoot>
                            <Form.Error name={path('name')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('goal')}>Goal</Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={'goal'}
                                inputRootProps={{ mask: 'decimal' }}
                            >
                                <InputCurrencyIcon />
                                <Form.TextInputForm />
                            </Form.TextInputRoot>
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('slug')}>Slug</Form.Label>
                            <Form.TextInputRoot control={control} name={'slug'}>
                                <Form.TextInputForm />
                            </Form.TextInputRoot>
                        </Form.Container>
                        <Form.Container className={'gap-2'}>
                            <Form.Label name={path('logo')}>Logo</Form.Label>
                            <UseFormUpload
                                inputRef={ref}
                                className={'min-h-[200px]'}
                                name={'logo'}
                                control={control}
                                acceptedFileTypes={['image/*']}
                                strategy={'remote'}
                                onactivatefile={() => {
                                    ref.current?.browse();
                                }}
                                allowReorder={false}
                                allowMultiple={false}
                                allowReplace={true}
                                allowRemove={!teamId}
                                maxFileSize={'500KB'}
                            />
                            <p className={'text-sm text-gray-600'}>
                                Recommended logo size: 400 x 400. Max size: 500 KB.
                            </p>
                            {!!teamId && team?.logo && (
                                <div>
                                    <Button
                                        variant="solid"
                                        size={'sm'}
                                        onClick={() => ref.current?.browse()}
                                    >
                                        Replace
                                    </Button>
                                </div>
                            )}
                        </Form.Container>
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel>Cancel</Drawer.Cancel>
                        <Form.SubmitButton isSubmitting={isLoading}>Save</Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

export { CampaignTeamDrawerForm };
